import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AuthShell, Field } from "./AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Eye, EyeOff, Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  // ResetPassword sends the user here with ?reset=1 after a successful reset.
  // Confirming it here rather than with a toast on the way out: the toaster
  // lives on the app shell, and a toast fired immediately before navigating
  // away is unreliable.
  const [params] = useSearchParams();
  const justReset = params.get("reset") === "1";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login({ email, password });
      navigate("/dashboard");
    } catch (err) {
      // Real backend now rejects bad credentials (and flags 2FA-enabled
      // accounts) instead of always succeeding, so this needs to surface.
      toast.error(
        err?.twoFactorRequired
          ? "This account has two-factor authentication enabled, which this preview UI doesn't support yet."
          : err?.message || "Invalid email or password"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell testId="login-page">
      <h1 className="font-display text-2xl font-semibold tracking-tight">Welcome back</h1>
      <p className="mt-1.5 text-sm text-muted-foreground">Sign in to your fleet command center.</p>

      {justReset && (
        <div className="mt-6 flex items-start gap-2 rounded-xl border border-emerald-400/25 bg-emerald-400/10 px-3 py-2.5 text-sm text-emerald-300" data-testid="login-reset-done">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Your password was updated. Sign in with your new password.</span>
        </div>
      )}

      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
        <Field label="Work email">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required data-testid="login-email" className="h-11" />
        </Field>
        <Field label="Password">
          <div className="relative">
            <Input type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="login-password" className="h-11 pr-10" />
            <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </Field>

        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <Checkbox defaultChecked /> Remember me
          </label>
          <Link to="/forgot-password" className="text-sm text-primary hover:underline" data-testid="forgot-link">Forgot password?</Link>
        </div>

        <Button type="submit" className="h-11 w-full" disabled={loading} data-testid="login-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="ml-1.5 h-4 w-4" /></>}
        </Button>
      </form>

      <p className="mt-6 text-center text-xs text-muted-foreground">
        Connected to the ARC Beacon API. Sign in with an account from your organization.
      </p>
    </AuthShell>
  );
}
